import { Button, Colors, Dialog, Icon, Intent, Spinner, Tag } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import CX from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import ImageHelpers from './Helpers'
import config from '../../config'
import { UprightStyle } from '../../types'
import { SortableImageListProps } from './sortableImageListTypes'

const DragHandle = SortableHandle(({ onClick }) => <div className="drag-handle" onClick={onClick} />)

const style: UprightStyle = {
  zoomBtn: { borderRadius: 0, position: 'absolute', bottom: 7, left: 8, color: 'white', zIndex: 1, cursor: 'pointer' },
  zoomDialog: { paddingBottom: 0, position: 'relative', overflow: 'hidden', width: '700px' },
  zoomExitBtn: {
    position: 'absolute',
    zIndex: 1,
    top: 17,
    right: 45,
    display: 'block'
  },
  imgContainer: {
    padding: '5px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center'
  },
  thumbnailGalleryItem: { maxHeight: '100px', justifySelf: 'center', display: 'inline' },
  thumbnailGallery: {
    display: 'grid',
    gridGap: '16px',
    padding: '16px',
    gridTemplateColumns: 'repeat(auto-fill,minmax(100px,1fr))',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(100px,1fr)',
    overflowX: 'auto'
  },
  zoomableGalleryDiv: {
    display: 'grid',
    gridTemplateColumns: '40px auto 40px'
  },
  mainZoomableGalleryDiv: {
    maxWidth: '620px',
    maxHeight: '620px'
  },
  mainZoomableGalleryImage: {
    border: 'solid #A7B6C2 2px',
    height: '100%',
    maxHeight: '620px',
    maxWidth: '620px'
  }
}

const ThumbnailGalleryItem = ({ url, index, currentImageIndex, setCurrentImageIndex, rotation }) => {
  const ref = useRef<HTMLDivElement>()
  useEffect(() => {
    const { current } = ref
    if (index === currentImageIndex && current) {
      current?.scrollIntoView()
    }
  }, [index, currentImageIndex])

  const hackRotation = ImageHelpers.hackRotationFn(url)

  return (
    <div onClick={() => setCurrentImageIndex(index)} ref={ref} style={style.thumbnailGalleryItem}>
      <FailableImage
        src={url}
        className="img-selection-img"
        alt={`number ${index + 1}`}
        style={{
          border: `2px solid ${currentImageIndex === index ? ' #337dff' : ' #A7B6C2'}`,
          transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)`
        }}
      />
    </div>
  )
}

const ThumbnailGallery = ({ items, currentImageIndex, setCurrentImageIndex }) => {
  const thumbnailItems = items.map((item, index) => {
    return (
      <ThumbnailGalleryItem
        url={item.url}
        index={index}
        currentImageIndex={currentImageIndex}
        setCurrentImageIndex={setCurrentImageIndex}
        rotation={item.rotation}
      />
    )
  })
  return <div style={style.thumbnailGallery}>{thumbnailItems}</div>
}

export const ZoomableGallery = ({ currentImageIndex, setCurrentImageIndex, items }) => {
  const [src, setSrc] = useState('')
  const [rotation, setRotation] = useState(0)

  useEffect(() => {
    const currentImage = items[currentImageIndex]

    if (currentImage !== undefined) {
      setSrc(currentImage.url || currentImage.preview)
      setRotation(currentImage.rotation)
    }
  }, [currentImageIndex, items])

  const handleDelta = (value, delta, max, min) => {
    const newValue = value + delta
    const inRange = newValue > min && newValue < max
    return inRange ? newValue : value
  }

  useEffect(() => {
    const keyDown = e => {
      switch (e.key) {
        case 'ArrowLeft':
          setCurrentImageIndex(v => handleDelta(v, -1, items.length, -1))
          break
        case 'ArrowRight':
          setCurrentImageIndex(v => handleDelta(v, 1, items.length, -1))
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', keyDown)
    return () => {
      document.removeEventListener('keydown', keyDown)
    }
  }, [currentImageIndex, setCurrentImageIndex, items])

  const onClose = () => {
    setCurrentImageIndex()
  }

  const hackRotation = ImageHelpers.hackRotationFn(src)

  return (
    <Dialog
      style={style.zoomDialog}
      canEscapeKeyClose
      canOutsideClickClose
      onClose={onClose}
      isOpen={currentImageIndex !== undefined}
    >
      <div style={style.zoomableGalleryDiv}>
        <Icon
          icon={IconNames.CARET_LEFT}
          iconSize={Icon.SIZE_LARGE}
          onClick={() => setCurrentImageIndex(v => handleDelta(v, -1, items.length, -1))}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: currentImageIndex === 0 ? '#A7B6C2' : undefined
          }}
        />
        <Icon icon={IconNames.CROSS} onClick={onClose} className="base-image-icon" style={style.zoomExitBtn} />
        <div style={{ paddingTop: '10px' }}>
          <div
            style={{
              height: '620px',
              width: '620px',
              transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div style={style.mainZoomableGalleryDiv}>
              <FailableImage src={src} alt={`number ${currentImageIndex + 1}`} style={style.mainZoomableGalleryImage} />
            </div>
          </div>
        </div>
        <Icon
          icon={IconNames.CARET_RIGHT}
          iconSize={Icon.SIZE_LARGE}
          onClick={() => setCurrentImageIndex(v => handleDelta(v, 1, items.length, -1))}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: currentImageIndex === items.length - 1 ? '#A7B6C2' : undefined
          }}
        />
      </div>
      <ThumbnailGallery
        items={items}
        currentImageIndex={currentImageIndex}
        setCurrentImageIndex={setCurrentImageIndex}
      />
    </Dialog>
  )
}

export const FailableImage = ({ src, ...props }) => {
  const [failed, setfailed] = useState(false)

  return (
    <>
      {failed || src === undefined ? (
        <div className="image-404">
          <Icon color={Colors.RED3} iconSize={24} icon="warning-sign" />
          <p className="bp3-text-small bp3-text-muted">This image can not be found, please remove and try again.</p>
        </div>
      ) : (
        <img
          src={src}
          {...props}
          alt=""
          onError={() => {
            setfailed(true)
          }}
        />
      )}
    </>
  )
}

export const SortableItem = SortableElement(
  ({
    value,
    ii,
    rotation,
    rotateImage,
    removeImage,
    editImage,
    revertImageEdit,
    loading,
    progress,
    failed,
    isDisabled = false,
    isEdited = false,
    disableEdit = false,
    setCurrentImageIndex
  }) => {
    const cn = CX('img-selection bp3-elevation-1')

    if (failed) {
      return (
        <div className={cn} style={style.imgContainer}>
          <p className="bp3-text-muted">Image Upload Failed</p>
          <Button
            small
            title="Remove image"
            onClick={() => removeImage(ii)}
            className="remove-image-icon"
            icon={IconNames.DELETE}
          />
        </div>
      )
    }

    const hackRotation = ImageHelpers.hackRotationFn(value)

    return (
      <div className={cn} style={style.imgContainer}>
        {loading ? (
          <Spinner value={progress} />
        ) : (
          <FailableImage
            src={value}
            className="img-selection-img"
            alt={`number ${ii + 1}`}
            style={{ transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)` }}
          />
        )}
        {ii === 0 && !loading && (
          <Tag className="main-img" intent={Intent.PRIMARY}>
            Main Image
          </Tag>
        )}
        {!loading && !isDisabled && <DragHandle onClick={() => setCurrentImageIndex(ii)} />}
        {!loading && !isDisabled && (
          <Button
            small
            title="Remove image"
            onClick={() => removeImage(ii)}
            className="remove-image-icon"
            icon={IconNames.DELETE}
          />
        )}
        {!loading && !isDisabled && (
          <Button
            disabled={isEdited} // dont allow images to be double rotated
            small
            title={
              isEdited
                ? "You can't use this button if you've used the image editor. Click the pencil to rotate instead."
                : 'Rotate image'
            }
            onClick={() => rotateImage(ii)}
            className="rotate-image-icon"
            icon={IconNames.REFRESH}
          />
        )}
        {!loading && !isDisabled && (
          <Button
            onClick={() => setCurrentImageIndex(ii)}
            tabIndex={0}
            style={style.zoomBtn}
            small
            icon={IconNames.ZOOM_IN}
            className="base-image-icon"
          />
        )}

        {config.IMAGE_EDITOR() && !loading && isEdited && (
          <Button
            title="Undo image edits"
            small
            icon="undo"
            className="revert-edit-link"
            intent={Intent.NONE}
            onClick={() => revertImageEdit(ii)}
          />
        )}

        {config.IMAGE_EDITOR() && !loading && !isDisabled && (
          <Button
            small
            icon="edit"
            title="Edit image"
            className="edit-link"
            intent={Intent.NONE}
            onClick={() => editImage(ii)}
            disabled={disableEdit}
          />
        )}
      </div>
    )
  }
)

const SortableList = SortableContainer(
  ({
    removeImage,
    rotateImage,
    revertImageEdit,
    editImage,
    items,
    isDisabled = false,
    disableEdit,
    children
  }: SortableImageListProps) => {
    const [currentImageIndex, setCurrentImageIndex] = useState()

    return (
      <div id="image-gallery-container" className="image-gallery-container">
        <ZoomableGallery
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
          items={items}
        />
        {children}
        {items.map((f, i) => {
          return (
            <SortableItem
              axis="xy"
              key={f.id || f.key || f.preview || i}
              index={i}
              ii={i}
              value={f.url || f.preview}
              loading={f.loading}
              failed={f.failed}
              progress={f.progress}
              rotation={f.rotation}
              removeImage={removeImage}
              rotateImage={rotateImage}
              revertImageEdit={revertImageEdit}
              isDisabled={isDisabled}
              editImage={editImage}
              isEdited={!!f.isEdited}
              disableEdit={disableEdit}
              setCurrentImageIndex={setCurrentImageIndex}
            />
          )
        })}
      </div>
    )
  }
)

export default SortableList
